import Link from "next/link";
import { resolveLocalizedValue } from "../utils/localization";
import type { LearningSectionItem } from "../types/learning";

type CategoryTileProps = {
  section: LearningSectionItem;
  locale: string;
};

/**
 * A single category tile: artwork with the name overlaid at the bottom.
 * Shared by the homepage/"View All Categories" grid (`Sections`) and the
 * sub-categories grid on a Parent Category's own page, so both stay visually
 * identical instead of drifting apart.
 */
export default function CategoryTile({ section, locale }: CategoryTileProps) {
  const sectionName = resolveLocalizedValue(section.name, locale);

  return (
    <Link
      href={`/categories/${section.slug}`}
      className="group relative block overflow-hidden rounded-2xl bg-primary shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] w-full">
        {section.image ? (
          <img
            src={section.image}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center bg-secondary text-6xl font-bold text-white/90">
            {sectionName.charAt(0).toUpperCase()}
          </span>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/25 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
          <h3 className="text-[17px] font-bold leading-snug text-white sm:text-xl">
            {sectionName}
          </h3>
          <span className="mt-1 block h-0.5 w-0 rounded-full bg-[#FFBB00] transition-all duration-300 group-hover:w-10" />
        </div>
      </div>
    </Link>
  );
}
